import React, { useCallback, useEffect, useMemo } from 'react'
import { Col, Form, Row, Spin } from 'antd'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormCheckbox,
  FormSelect,
  FormTextareaInput,
} from '../../../common/components/sections'
import {
  StyledPageHeader,
  StyledPageTitle,
} from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import {
  createSelectOptions,
  showErrorNotification,
} from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { idPropType } from '../../../common/prop-types'
import { generateArrayOfNumbers } from '../../../common/utils'

import { useUpdateLocationTypeSimpleDetailsMutation } from '../../hooks'

import { locationTypeSimpleDetailsFormInitialValues } from './initial-values'

const LocationTypeSimpleDetailsForm = ({
  id,
  name,
  description,
  synonyms,
  isExcludedFromSearch,
  isExcludedFromBrowsing,
  position,
  maxPosition,
}) => {
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.LOCATION_TYPE.UPDATE_DETAILS,
  ])

  const [
    updateLocationTypeDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateLocationTypeSimpleDetailsMutation()

  const initialValues = useMemo(
    () =>
      locationTypeSimpleDetailsFormInitialValues({
        description,
        synonyms,
        isExcludedFromSearch,
        position,
        isExcludedFromBrowsing,
      }),
    [
      description,
      isExcludedFromSearch,
      isExcludedFromBrowsing,
      position,
      synonyms,
    ],
  )

  const positionOptions = useMemo(
    () => createSelectOptions(generateArrayOfNumbers(maxPosition)),
    [maxPosition],
  )

  const handleSaveData = useCallback(
    data => updateLocationTypeDetails(id, data),
    [id, updateLocationTypeDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Location Type description error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [description, synonyms, position, resetForm])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="editLocationType"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <StyledPageHeader>
        <StyledPageTitle>{name}</StyledPageTitle>
      </StyledPageHeader>

      <Row gutter={[16]}>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="description"
            label="Description"
            labelAbove
            placeholder="Input description..."
            autoSize
          />
        </Col>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="synonyms"
            label="Synonyms (separated by new line)"
            labelAbove
            placeholder="Input synonyms..."
            autoSize
          />
        </Col>
        <Col span={12}>
          <FormSelect
            disabled={!isFormEnabled}
            name="position"
            labelAbove
            label="Position"
            showSearch={false}
            allowClear={false}
            options={positionOptions}
            placeholder="Select position..."
          />
        </Col>
        <Col span={12}>
          <FormCheckbox
            disabled={!isFormEnabled}
            label="Exclude from mobile app search"
            name="isExcludedFromSearch"
          />
          <FormCheckbox
            disabled={!isFormEnabled}
            label="Exclude from mobile app browsing"
            name="isExcludedFromBrowsing"
          />
        </Col>
      </Row>
      <FormButtons
        permissions={[PERMISSIONS.LOCATION_TYPE.UPDATE_DETAILS]}
        isDisabled={!isDirty}
        onReset={resetForm}
      />
    </DirtyPromptForm>
  )
}

LocationTypeSimpleDetailsForm.propTypes = {
  id: idPropType.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  synonyms: PropTypes.string,
  position: PropTypes.number.isRequired,
  maxPosition: PropTypes.number.isRequired,
  isExcludedFromSearch: PropTypes.bool.isRequired,
  isExcludedFromBrowsing: PropTypes.bool.isRequired,
}

LocationTypeSimpleDetailsForm.defaultProps = {
  description: undefined,
  synonyms: undefined,
}

export default LocationTypeSimpleDetailsForm
